<template>
  <div
    v-if="dataLoaded"
    class="container"
  >
    <div class="row c-justify-center">
      <!-- title, breadcrumb, back to media -->
      <div class="c-md:col-10 c-xl:col-8 c-mb-5 c-lg:c-mb-7 text-center">
        <BreadCrumb
          :breadcrumb-list="breadcrumbList"
          additional-class-name="c-justify-center"
        />
        <h1
          v-if="medium?.title"
          class="heading-4 c-mb-3"
        >
          {{ medium.title }}
        </h1>
        <h1
          v-else
          class="heading-4 c-mb-3"
        >
          {{ t('media') }}
        </h1>
        <p class="c-mb-3 text-sm-c">
          {{ t('source') }}:
          <a
            v-if="medium?.link"
            :href="medium.link"
            target="_blank"
          >
            {{ medium.source }}
          </a>
          <span v-else>{{ medium.source }}</span>
        </p>
        <router-link
          :to="{ name: 'mediaPath' }"
          class="c-mt-2 btn btn-outline-primary border-0 font-medium c-rounded-pill"
        >
          <i class="mi-chevron-l c-mr-2" />
          {{ t('media') }}
        </router-link>
      </div>

      <div
        v-if="medium?.link"
        class="col-12"
      >
        <a
          :href="medium.link"
          target="_blank"
          class="row c-justify-center"
        >
          <RemoteResponsiveImage
            :product-image-data="medium.image"
            img-class="c-w-100 c-h-100"
            class="col-4"
            :alt="medium.title"
            sizes="(min-width: 1200px) 25vw, (min-width: 600px) 30vw, (min-width: 500px) 30vw, 50vw"
            :lazy-load="false"
          />
        </a>
      </div>
      <div
        v-else
        class="col-12 text"
      >
        <ul class="media_main_catalog row no-gutters list-unstyled c-justify-center c-px-4 cursor-pointer">
          <li
            class="media_main_catalog-img col-4 c-m-2"
            @click="
              openModal({
                id: 'modal-media-image',
                title: medium.title,
                params: {
                  image: medium.image,
                },
              })
            "
          >
            <RemoteResponsiveImage
              :product-image-data="medium.image"
              img-class="c-w-100 c-h-100"
              :alt="medium.title"
              sizes="(min-width: 1200px) 25vw, (min-width: 600px) 30vw, (min-width: 500px) 30vw, 50vw"
              :lazy-load="false"
            />
          </li>
          <li
            v-for="attachment in medium.attachments"
            :key="attachment"
            class="media_main_catalog-img col-4 c-m-2 cursor-pointer"
            @click="
              openModal({
                id: 'modal-media-image',
                title: medium.title,
                params: {
                  image: attachment,
                },
              })
            "
          >
            <RemoteResponsiveImage
              :product-image-data="attachment"
              img-class="c-w-100 c-h-100"
              :alt="medium.name"
              sizes="(min-width: 1200px) 25vw, (min-width: 600px) 30vw, (min-width: 500px) 30vw, 50vw"
            />
          </li>
        </ul>
      </div>
      <!-- text content -->
      <div
        v-if="medium?.text"
        class="c-lg:col-10 c-xl:col-8 c-pt-5 c-md:c-pt-6 c-lg:c-pt-7"
      >
        <div
          class="text"
          v-html="medium.text"
        />
      </div>
    </div>
  </div>
  <BaseLoading
    v-else
    :style="{ minHeight: '40vh' }"
  />
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import BreadCrumb from '@/components/general/BaseBreadcrumb.vue'
import BaseLoading from '@/components/general/BaseLoading.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { useModalStore } from '@/stores/ModalStore'

const props = defineProps({
  medium: {
    type: Object,
    required: true,
  },
  dataLoaded: {
    type: Boolean,
    default: false,
  }
})

const { openModal } = useModalStore()
const route = useRoute()
const { t } = useI18n()

const breadcrumbList = computed(() => {
  const list = []
  list.push(
    {
      name: t('media'),
      link: { name: 'mediaPath' },
    },
    {
      name: props.medium.source + (props.medium.source && props.medium.title ? ': ' : '') + props.medium.title,
      link: route.fullPath,
    },
  )
  return list
})
</script>

<style lang="scss" scoped>
.media_main_catalog {
  &-img {
    width: 375px;
    height: 500px;
    min-height: 150px;
  }
}

@media (max-width: 992px) {
  .media_main_catalog {
    &-img {
      width: 219px;
      height: 292px;
    }
  }
}

@media (max-width: 768px) {
  .media_main_catalog {
    &-img {
      width: 159px;
      height: 212px;
    }
  }
}

@media (max-width: 576px) {
  .media_main_catalog {
    &-img {
      width: 100%;
      height: 100%;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "media": "Média",
    "source": "Zdroj"
},
    "sk": {
    "media": "Médiá",
    "source": "Zdroj"
},
    "pl": {
    "media": "Media",
    "source": "Źródło"
},
    "hu": {
    "media": "Megjelenéseink",
    "source": "Forrás"
},
    "de": {
    "media": "Medien",
    "source": "Quelle"
},
    "at": {
    "media": "Medien",
    "source": "Quelle"
},
    "ro": {
    "media": "Media",
    "source": "Sursa"
},
    "fr": {
    "media": "Médias",
    "source": "Source"
},
    "it": {
    "media": "Media",
    "source": "Fonte"
}
  }
</i18n>
